import { Box, VStack, HStack, Grid, Separator, Text } from "@chakra-ui/react";
import { BiPlus } from "react-icons/bi";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import SessionModal from "./SessionModal";
import { Session } from "../../../../services/Dtos";
import { ChangeEvent, useEffect, useState } from "react";
import Loader from "../../../CustomUi/Loader";
import CustomAlert from "../../../CustomUi/CustomAlert";
import { formatDate, getNextOrder } from "../../../../services/Utils";
import SessionCard from "./SessionCard";
import Empty from "../../../CustomUi/Empty";
import useThemeColors from "../../../../hooks/useThemeColors";
import SectionHeader from "../../../CustomUi/SectionHeader";
import useIcons from "../../../../hooks/useIcons";
import Cad from "../../../CustomUi/Cad";
import CustomButton from "../../../CustomUi/CustomButton";
import SelectInput from "../../../CustomUi/SelectInput";
import { useDisclosure } from "@chakra-ui/react";
import { useSchoolService } from "../../../../hooks/Domain/useSchoolService";

function SessionSettings() {
  const { school, sessions, isLoading, error, accentColor } =
    useSchoolsContext();
  const [sessionToEdit, setSessionToEdit] = useState<Session | null>(null);
  const { onOpen, open: isOpen, onClose } = useDisclosure();
  const [currentSession, setcurrentSession] = useState<string | undefined>(
    undefined
  );
  const colors = useThemeColors();
  const [nextOrder, setNextOrder] = useState(0);
  const icons = useIcons();
  const { updateCurrentSession, currentSessionLoading } = useSchoolService();

  useEffect(() => {
    setcurrentSession(school?.currentSessionId);
  }, [sessions]);

  const handleAddSession = () => {
    setSessionToEdit(null);
    setNextOrder(getNextOrder(sessions));
    onOpen();
  };

  const handleEditStudent = (session: Session) => {
    const modifiedSession = {
      ...session,
      sessionStartDate: formatDate(session.sessionStartDate),
      sessionEndDate: formatDate(session.sessionEndDate),
    };
    setSessionToEdit(modifiedSession);
    onOpen();
  };

  const onCurrentSessionChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setcurrentSession(e.target.value);
  };

  const handleCurrentSessionSave = async () => {
    if (!currentSession) {
      return;
    }
    await updateCurrentSession(currentSession);
     
      
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <CustomAlert message={error} handleClick={() => {}} />;
  }

  const sessionOptions = sessions.map((s) => ({
    label: s.sessionName,
    value: s.id,
  }));

  return (
    <VStack gap={8}>
      <SessionModal
        isOpen={isOpen}
        onClose={onClose}
        sessionToEdit={sessionToEdit}
        nextOrder={nextOrder}
      />

      {/* Sessions Management Section */}
      <Cad>
        <VStack gap={6} align="stretch">
          <SectionHeader
            title="Academic Sessions"
            subtitle="Create and manage your school's academic sessions"
            icon={icons.main.session}
            buttonText="Add Session"
            onButtonClick={handleAddSession}
            buttonIcon={BiPlus}
            buttonColorScheme={accentColor}
          />

          <Separator />

          {/* Sessions Grid */}
          {sessions.length > 0 ? (
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                xl: "repeat(3, 1fr)",
              }}
              gap={6}
            >
              {sessions.map((s) => (
                <SessionCard
                  key={s.id}
                  session={s}
                  handleClick={() => handleEditStudent(s)}
                />
              ))}
            </Grid>
          ) : (
            <Box py={8}>
              <Empty
                message="No sessions created yet"
                hasButton
                buttonText="Create First Session"
                handleClick={handleAddSession}
              />
            </Box>
          )}
        </VStack>
      </Cad>

      {/* Current Session Selection */}
      {sessions.length > 0 && (
        <Cad>
          <VStack gap={6} align="stretch">
            <SectionHeader
              title="Current Session"
              subtitle="Set the active session for result calculations"
            />

            <Separator />

            <VStack gap={4} align="stretch">
              <Box>
                <Text fontSize="sm" color={colors.text.secondary} mb={4}>
                  Select the current session for the school. This session will
                  be used for all result computations. To calculate results for
                  a different session, simply switch the current session here.
                </Text>
              </Box>

              <HStack
                gap={4}
                w="full"
                justifyContent="space-between"
                flexDirection={["column", "row"]}
                alignItems={["stretch", "center"]}
              >
                <VStack gap={2} flex={1} w="full">
                  <SelectInput
                    collection={sessionOptions}
                    value={currentSession}
                    onChange={(e) => onCurrentSessionChange(e)}
                    label=""
                    name="currentSession"
                    placeholder="Choose a session"
                  />
                </VStack>
                <CustomButton
                  colorPalette={accentColor}
                  label="Save Current Session"
                  onClick={handleCurrentSessionSave}
                  loading={currentSessionLoading}
                  disabled={!currentSession}
                  variant="outline"
                />
              </HStack>
            </VStack>
          </VStack>
        </Cad>
      )}
    </VStack>
  );
}

export default SessionSettings;
